import type { AgentToolRunContext } from "@aiverse/core";
import type { Browser, BrowserContextOptions, LaunchOptions } from "playwright";

export const BROWSER_TOOL_NAMES = [
	"browser-open",
	"browser-navigate",
	"browser-back",
	"browser-snapshot",
	"browser-follow-link",
	"browser-click",
	"browser-type",
	"browser-submit",
	"browser-screenshot",
	"browser-close",
] as const;

export type BrowserToolName = (typeof BROWSER_TOOL_NAMES)[number];

export type BrowserAction =
	| "open"
	| "navigate"
	| "back"
	| "snapshot"
	| "follow-link"
	| "click"
	| "type"
	| "submit"
	| "screenshot"
	| "close";

export type BrowserReceiptStatus =
	| "ok"
	| "blocked"
	| "denied"
	| "unavailable"
	| "error"
	| "closed";

/**
 * The deliberately small browser result that may be streamed to the UI and
 * persisted in conversation history. Page snapshots, screenshots, entered
 * text, and remote viewer URLs are never included here.
 */
export type BrowserReceipt = {
	aiverseKind: "browser";
	action: BrowserAction;
	status: BrowserReceiptStatus;
	label: string;
	url?: string;
	title?: string;
	message?: string;
	liveViewId?: string;
	refCount?: number;
	truncated?: boolean;
	bytes?: number;
};

export type BrowserRunDisposeReason = "complete" | "abort" | "error" | "close";

export type RemoteBrowserLiveView = {
	/** Resolve a fresh raw/signed viewer URL. It is handed only to core's broker. */
	resolve: () => string | Promise<string>;
	/** Epoch milliseconds. Defaults to the lifetime of the active root run. */
	expiresAt?: number;
	/** Opt in only when a development viewer really is served over local HTTP. */
	allowInsecureLocalhost?: boolean;
};

/** A host-managed browser lease returned once per root run, on first use. */
export type RemoteBrowserSession = {
	browser: Browser;
	liveView?: RemoteBrowserLiveView;
	/** Release the host lease. The adapter never closes a remote Browser itself. */
	release?: (reason: BrowserRunDisposeReason) => void | Promise<void>;
};

export type LocalBrowserConnection = {
	mode?: "local";
	/** Headed by default so development runs are observable. */
	headless?: boolean;
	/** Passed to chromium.launch(); persistent-context APIs are never used. */
	launchOptions?: Omit<LaunchOptions, "headless">;
};

export type RemoteBrowserConnection = {
	mode: "remote";
	connect: (
		context: AgentToolRunContext,
	) => RemoteBrowserSession | Promise<RemoteBrowserSession>;
};

export type BrowserLimits = {
	/** Top-level navigation timeout. Default 30,000 ms. */
	navigationTimeoutMs?: number;
	/** Element action and snapshot timeout. Default 5,000 ms. */
	actionTimeoutMs?: number;
	/** Maximum UTF-8 bytes exposed in an accessibility snapshot. Default 32 KiB. */
	maxSnapshotBytes?: number;
	/** Maximum raw PNG bytes exposed to the model. Default 1 MiB. */
	maxScreenshotBytes?: number;
	/** Maximum interactive opaque references retained per snapshot. Default 200. */
	maxRefs?: number;
};

export type PlaywrightToolProviderOptions = {
	/** Capability/provider id. Custom agents grant this id. Default "browser". */
	id?: string;
	/**
	 * Exact normalized HTTP(S) origins allowed to resolve to otherwise blocked
	 * private addresses, for example http://localhost:4111 in development.
	 */
	allowedOrigins?: readonly string[];
	/** Defaults to a locally launched, headed Chromium process. */
	connection?: LocalBrowserConnection | RemoteBrowserConnection;
	/**
	 * Options for the fresh incognito context created per root run. Storage,
	 * downloads, service workers, HAR, and video persistence are controlled by
	 * the adapter.
	 */
	contextOptions?: Omit<
		BrowserContextOptions,
		| "storageState"
		| "acceptDownloads"
		| "serviceWorkers"
		| "recordHar"
		| "recordVideo"
	>;
	limits?: BrowserLimits;
};
